import { cn } from 'cn';
import { type PropsWithChildren } from 'react';

import { Icon } from '../../atoms/Icon';
import { Tooltip } from '../Tooltip';

type P = PropsWithChildren<{
  readonly hint?: string;
  readonly htmlFor?: string;
  readonly isRequired?: boolean;
}> &
  ComponentStyling;

/**
 * Component for displaying a label for a form field.
 * @param children - The content to display.
 * @param className - The class name to apply to the component.
 * @param hint - An additional hint to display.
 * @param htmlFor - The HTML for attribute for linking the label to the related input.
 * @param isRequired - Whether the field is required.
 * @param style - The style to apply to the component.
 * @returns
 */
export const InputLabel = ({
  children,
  className,
  hint,
  htmlFor,
  isRequired,
  style,
}: P) => (
  <label
    className={cn('block text-sm text-tiga-text-default', className)}
    htmlFor={htmlFor}
    style={style}
  >
    {children}
    {isRequired ? (
      <span className="pl-[0.25ch] font-bold text-tiga-semantic-danger">*</span>
    ) : null}

    {hint ? (
      <Tooltip content={hint}>
        <span className="inline-block -translate-y-0.25 pl-0.5">
          <Icon
            className="cursor-help text-tiga-action-default transition-colors duration-300 hover:text-tiga-action-lighter"
            metaphor="infoOutlined"
            size="sm"
          />
        </span>
      </Tooltip>
    ) : null}
  </label>
);
